/** @typedef {import('./types').MoxiesSelectOption} MoxiesSelectOption */
import { decode } from "he";

const svgIcon = `<svg viewBox="0 0 22 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.6133 11.0596L21.1152 1.55762L20.4114 0.853769L11.0479 10.2173L10.0523 10.2173L0.703638 0.853768L-0.000210264 1.55762L9.50174 11.0596C9.64031 11.1983 9.80489 11.3084 9.98605 11.3836C10.1672 11.4587 10.3614 11.4973 10.5575 11.4973C10.7536 11.4973 10.9478 11.4587 11.129 11.3836C11.3101 11.3084 11.4747 11.1983 11.6133 11.0596Z" fill="currentColor"></path></svg>`;

/**
 * Custom Select element for forms
 * This is a custom select box. To use simply wrap native `select` element
 * complete with `option`s. Be sure to set a value. The select value will still
 * be readable by `FormData`, but the presentation will be completely customizable.
 *
 * @example
 * <mx-select>
 *   <select name="my-select" disabled="true" value="">
 *      <option>Select an occasion</option>
 *      <option value="birthday">Birthday</option>
 *      <option value="anniversary">Anniversary</option>
 *      <option value="business_meal">Business Meal</option>
 *      <option value="celebration">Celebration</option>
 *   </select>
 * </mx-select>
 *
 * @class MoxiesSelect
 * @extends HTMLElement
 * @prop {string} value
 */
class MoxiesSelect extends HTMLElement {
  static observedAttributes = ["value", "items"];
  /** @type {MoxiesSelectOption[]} */
  _options = [];

  _emptyText = "";

  constructor() {
    super();
    // Nested trigger button
    this.button = this.querySelector("button.moxies-button");
    // Holds the options
    this.dropdownContainer = document.createElement("ul");
    this.dropdownContainer.classList.add("dropdownContainer__dropdown");
    this.dropdownContainer.setAttribute("role", "menulist");
    // Stores the FormDataValue
    this.inputEl = document.createElement("input");
    this.inputEl.setAttribute("name", this.id);
    this.inputEl.style.opacity = "0";
    this.inputEl.hidden = true;
    this.inputEl.ariaHidden = "true";
    this.inputEl.value = "";

    // Button isn't guaranteed to be there if it's a nested select
    this.button?.addEventListener("click", this);
    this.addEventListener("mousedown", this);
    this.addEventListener("focusout", this);
  }

  connectedCallback() {
    const select = this.querySelector("select");
    const generatedButton = this.querySelector("button.mx-select-button");

    if (select && !generatedButton) {
      /** @type MoxiesSelectOption[] */
      const options = [];
      select.querySelectorAll("option")?.forEach((option) => {
        options.push({
          label: option.textContent,
          value: option.getAttribute("value"),
        });
      });
      this.options = options;
      this.inputEl.setAttribute("name", select.name);
      select.style.setProperty("display", "none");
      // this.removeChild(select);
      // Add a button if overriding a select
      const button = document.createElement("button");
      button.type = "button";
      button.classList.add("mx-select-button");
      button.addEventListener("click", this);

      const labelEl = document.createElement("span");
      const labelText = this.options[0]?.label;
      labelEl.setAttribute("mx-select-label", "");
      labelEl.textContent = decode(labelText);
      this._emptyText = labelText;
      button.appendChild(labelEl);

      if (this.hasAttribute("show-icon")) {
        const iconEl = document.createElement("span");
        iconEl.setAttribute("mx-select-icon", "");
        iconEl.innerHTML = svgIcon;
        button.appendChild(iconEl);
      }

      this.appendChild(button);
      if (select.value) {
        this.setAttribute("value", select.value);
      }
    }
    this.appendChild(this.inputEl);
    this.appendChild(this.dropdownContainer);
  }

  /**
   * @param {string} key
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(key, oldValue, newValue) {
    const options = this._options;
    if (key === "value" && oldValue !== newValue) {
      const label = this.querySelector("[mx-select-label]");
      if (label) {
        label.childNodes.forEach((node, index) => {
          if (index === 0) {
            if (node.nodeType === node.TEXT_NODE) {
              const labelText = options.reduce((memo, option) => {
                if (newValue == option.value) {
                  return option.label;
                }

                return memo;
              }, newValue);
              node.textContent = labelText
                ? decode(labelText)
                : this._emptyText;
            }
          }
        });
        this.inputEl.value = newValue;
      }
    }
    if (key === "items" && newValue && oldValue !== newValue) {
      this.options = JSON.parse(newValue);
    }
  }

  /**
   * @param {Event} event
   */
  handleEvent(event) {
    this[`handle${event.type}`](event);
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  handleclick(event) {
    const target = /** @type {(HTMLDivElement | HTMLButtonElement)} */ (
      event.target
    );
    if (target.localName === "button") {
      event.preventDefault();
      if (this.classList.toggle("open")) {
        // this.dropdownContainer.focus();
      }
    }
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  handlemousedown(event) {
    const target = /** @type {(HTMLDivElement | HTMLButtonElement)} */ (
      event.target
    );
    if (target.hasAttribute("data-mx-option")) {
      const value = target.getAttribute("data-value");
      this.inputEl.value = value;
      if (value) {
        this.setAttribute("value", value);
      }
      const changeEvent = new Event("change", {
        bubbles: true,
        cancelable: true,
      });
      this.dispatchEvent(changeEvent);
      this.classList.remove("open");
    }
  }

  /**
   * @param {FocusEvent} event
   */
  handlefocusout(event) {
    if (this.classList.contains("open") || !event.relatedTarget) {
      this.classList.remove("open");
    }
  }

  /**
   * @param {MoxiesSelectOption[]} value
   */
  set options(value) {
    this._options = value;
    this.dropdownContainer.innerHTML = "";

    value.forEach((option) => {
      const opt = document.createElement("li");
      opt.classList.add("dropdownContainer__dropdownItem");
      opt.setAttribute("data-mx-option", "true");
      if (option.value) {
        opt.setAttribute("data-value", option.value);
      }
      opt.textContent = decode(option.label);
      this.dropdownContainer.appendChild(opt);
    });
  }

  /**
   * @returns {MoxiesSelectOption[]}
   */
  get options() {
    return this._options;
  }
}

export default MoxiesSelect;

customElements.define("mx-select", MoxiesSelect);
